import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { LoaDocument } from '../../../../models/loa-document.model';
import { LegalEntityService } from '../../../../services/legal-entity.service';
import { LoggerService } from '../../../../../../core/logging/logger.service';
import { SelectedLegalEntityService } from '../../../../../../shared/services/selected-legal-entity.service';
import { UserPermissionsService } from '../../../../../../core/auth/services/user-permissions.service';
import { StatusChangeModalComponent } from '../../../../../../shared/components/status-change-modal/status-change-modal.component';

const DELETABLE_DOCUMENT_TYPES = ['LOA (Signed)', 'LOI (Completed)'];

@Component({
  selector: 'app-loa-tracking',
  imports: [StatusChangeModalComponent],
  templateUrl: './loa-tracking.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './loa-tracking.component.scss',
})
export class LoaTrackingComponent implements OnInit, OnChanges {
  @Input() legalEntityId: number | null = null;

  loaDocuments: LoaDocument[] = [];
  documentDate: string = '';
  isDeleteModalOpen: boolean = false;
  private pendingDeleteDoc: LoaDocument | null = null;

  @ViewChild('deleteModal') deleteModal!: StatusChangeModalComponent;

  private legalEntityService = inject(LegalEntityService);
  private logger = inject(LoggerService);
  private selectedLegalEntity = inject(SelectedLegalEntityService);
  public userPermissions = inject(UserPermissionsService);

  canDeleteDocument(doc: LoaDocument): boolean {
    return (
      this.userPermissions.hasPstMemberRole() &&
      DELETABLE_DOCUMENT_TYPES.includes(doc.document_type)
    );
  }

  requestDeleteDocument(doc: LoaDocument): void {
    this.pendingDeleteDoc = doc;
    this.isDeleteModalOpen = true;
    setTimeout(() => {
      this.deleteModal.showCustomConfirm(
        'Delete Document',
        `Are you sure you want to delete "${doc.name}"?`,
        'Cancel',
        'Delete',
      );
    }, 0);
  }

  onDeleteConfirmed(): void {
    if (!this.legalEntityId || !this.pendingDeleteDoc) {
      return;
    }

    const doc = this.pendingDeleteDoc;
    this.deleteModal.state = 'processing';

    this.legalEntityService.deleteLoaDocument(this.legalEntityId, doc.document_file_id).subscribe({
      next: () => {
        const docLabel = doc.document_type.startsWith('LOI') ? 'LOI' : 'LoA';
        const successMessage = `${docLabel} document deleted successfully`;
        this.deleteModal.showSuccessWithMessage(successMessage, 'Delete Document');
        this.loadLoaDocuments();
      },
      error: (error) => {
        this.logger.error('Failed to delete LoA document', {
          document_file_id: doc.document_file_id,
          error,
        });
        this.deleteModal.showError(
          error?.error?.message ||
            'An error occurred while deleting the document. Please try again.',
          'Error',
          'error',
          [],
          'Delete Document',
        );
      },
    });
  }

  onDeleteCancelled(): void {
    this.isDeleteModalOpen = false;
    this.pendingDeleteDoc = null;
  }

  onDeleteModalClosed(): void {
    this.isDeleteModalOpen = false;
    this.pendingDeleteDoc = null;
  }

  ngOnInit(): void {
    this.loadLoaDocuments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload documents when legalEntityId Input changes
    if (changes['legalEntityId'] && !changes['legalEntityId'].firstChange) {
      this.loadLoaDocuments();
    }
  }

  /**
   * Load Letter of Adherence documents for the current legal entity
   */
  private loadLoaDocuments(): void {
    if (!this.legalEntityId) {
      this.logger.warn('Cannot load LoA documents: legalEntityId is null');
      return;
    }

    this.selectedLegalEntity.setLoaDocumentsLoading(true);

    this.legalEntityService.getLoaDocuments(this.legalEntityId).subscribe({
      next: (documents) => {
        this.loaDocuments = documents || [];
        this.updateDocumentDate();
        this.selectedLegalEntity.setLoaDocumentsLoading(false);
      },
      error: (error) => {
        this.logger.error('Failed to load LoA documents', { entityId: this.legalEntityId, error });
        this.loaDocuments = []; // Graceful degradation
        this.updateDocumentDate();
        this.selectedLegalEntity.setLoaDocumentsLoading(false);
      },
    });
  }

  /**
   * Update the documentDate property with the most recent document's upload date
   * Sets to empty string if no documents exist
   */
  updateDocumentDate(): void {
    if (!this.loaDocuments || this.loaDocuments.length === 0) {
      this.documentDate = '';
      return;
    }

    // Find the document with the most recent created_date
    const mostRecentDoc = this.loaDocuments.reduce((latest, current) => {
      return current.created_date > latest.created_date ? current : latest;
    });

    this.documentDate = this.formatDate(mostRecentDoc.created_date);
  }

  /**
   * Format Unix timestamp to DD/MMM/YYYY format
   * @param timestamp - Unix timestamp in seconds
   * @returns Formatted date string (e.g., "18/Feb/2024")
   */
  formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Download a LoA document by generating a presigned URL and triggering download
   * @param document - The LoA document to download
   */
  downloadDocument(document: LoaDocument): void {
    // Check if legalEntityId is valid
    if (!this.legalEntityId) {
      this.logger.error('Cannot download document: legalEntityId is not set');
      return;
    }

    // Generate download URL and trigger download
    this.legalEntityService
      .generateDownloadUrl(this.legalEntityId, document.document_file_id)
      .subscribe({
        next: (response) => {
          // Log successful download initiation
          this.logger.info('Downloading LoA document:', {
            document_file_id: document.document_file_id,
            document_name: document.name,
          });

          // Trigger browser download
          this.legalEntityService.downloadFile(response.download_url, document.name);
        },
        error: (error) => {
          // Log error
          this.logger.error('Error generating download URL for LoA document:', {
            document_file_id: document.document_file_id,
            document_name: document.name,
            error,
          });
        },
      });
  }
}
